import { BenefitLayout } from "providers/benefit-layout";
import React, { useState } from "react";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import { DateInput } from "shared/components/forms/DateInput";
import { Link, useParams } from "react-router-dom";
import { Button, ButtonVariant } from "shared/components/Button";
import { validateMoveOutDate } from "./validations";
import { useMutation } from "@tanstack/react-query";
import api from "api";
import { toast } from "react-toastify";
import { Hide } from "shared/components";
import { getErrorMessage } from "helpers/getErrorMessage";
import { usePostHog } from "posthog-js/react";
import { useFormTyped } from "shared/hooks/use-form-typed";
import { AxiosError } from "axios";

enum Views {
  ConfirmView = "ConfirmView",
  Success = "Success"
}

export const MoveOutFlow = () => {
  const { onSubmit, register } = useFormTyped(
    {
      moved_out_date: ""
    },
    validateMoveOutDate
  );
  const { id } = useParams();
  const [view, setView] = useState<Views>(Views.ConfirmView);

  const { mutate } = useMutation({
    mutationKey: ["CONFIRM_MOVE_OUT", id],
    mutationFn: (moved_out_date: string) => {
      return api
        .confirmMoveOut({ id: Number(id || 0), moved_out_date })
        .then(() => setView(Views.Success));
    },
    onError: (error: AxiosError) => {
      const readableMessages: Record<string, string | undefined> = {
        moved_out_too_far_in_the_future:
          "Move out date cannot be more than 7 days in the future",
        invalid_moved_out_date:
          "Move out date cannot be earlier than move in date"
      };

      const errorMessage = getErrorMessage(error, "Something went wrong");

      toast.error(readableMessages[errorMessage] || errorMessage);
    }
  });
  if (view === Views.ConfirmView)
    return (
      <BenefitLayout
        title="Enter your move out date"
        hasBack={true}
        mainClassName="mx-auto max-w-[440px] mb-6 md:mt-4 px-0"
        footer={
          <div className="max-w-[440px] mx-auto w-full">
            <Button
              variant={ButtonVariant.PRIMARY}
              className="w-full"
              type="submit"
              form="move_out_date_form"
            >
              Confirm
            </Button>
            <Link
              to={`/rent_reporting/${id}`}
              className="w-full md:block hidden mt-2"
            >
              <Button className="w-full">Back</Button>
            </Link>
          </div>
        }
      >
        <p className="text-sm text-content-600 mb-6">
          This should be the last day you were at this apartment. Your will be
          able to add your new apartment after
        </p>
        <form
          id="move_out_date_form"
          onSubmit={onSubmit({
            onSuccess: (values) => mutate(values.moved_out_date),
            onError: (error) => {
              toast.error(getErrorMessage(error));
            }
          })}
        >
          <DateInput
            id="moved_out_date"
            label="Move out date"
            {...register("moved_out_date")}
            hideOptional
          />
        </form>
      </BenefitLayout>
    );

  return <SuccessView />;
};

export const SuccessView = () => {
  const { featureFlags } = usePostHog();
  const isPartnerControlledAddNewAddress = Boolean(
    featureFlags?.isFeatureEnabled("partner-controlled-add-new-address")
  );

  return (
    <BenefitLayout
      title=""
      footer={
        <div className="max-w-[440px] mx-auto w-full">
          <Hide if={isPartnerControlledAddNewAddress}>
            <Link to="/rent_reporting/onboarding/intro" className="w-full mb-2">
              <Button variant={ButtonVariant.PRIMARY} className="w-full">
                Add new address
              </Button>
            </Link>
          </Hide>

          <Link to="/hub" className="w-full block">
            <Button
              variant={
                isPartnerControlledAddNewAddress
                  ? ButtonVariant.PRIMARY
                  : ButtonVariant.BASE
              }
              className="w-full"
            >
              {isPartnerControlledAddNewAddress ? "That's it!" : "Maybe later"}
            </Button>
          </Link>
        </div>
      }
    >
      <div className="pb-6 max-w-[440px] mx-auto">
        <SuccessIcon width={40} height={40} className="mb-6 text-success-500" />
        <h1 className="text-content-1000 text-xl font-bold mb-6">
          Success! You’ve moved out of your last address.
        </h1>
        <Hide if={isPartnerControlledAddNewAddress}>
          <p className="text-sm text-content-600">
            Add a new address to continue building credit?
          </p>
        </Hide>
      </div>
    </BenefitLayout>
  );
};
